// Thin fetch wrapper. The backend session cookie (bbk_session) is HttpOnly +
// same-origin via vite proxy, so we just need credentials: "include" to ship
// it on every request. No token-in-localStorage anti-pattern.

export class APIError extends Error {
  // detail carries the parsed JSON body (when the body was JSON) so
  // callers can pull structured fields the backend wanted to surface —
  // e.g. the EntitySelf 409 returns {error, total, ids[], resolve} so
  // the Conflict UI can list the conflicting entity IDs as direct
  // links. Without this, only the `error` string survives.
  constructor(
    public status: number,
    message: string,
    public detail?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {};
  let payload: BodyInit | undefined;
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }
  const res = await fetch(path, {
    method,
    headers,
    body: payload,
    credentials: "include",
  });
  if (!res.ok) {
    const text = await res.text();
    let msg = text;
    let detail: unknown;
    try {
      const j = JSON.parse(text);
      detail = j;
      if (j && typeof j === "object" && "error" in j && typeof j.error === "string") {
        msg = j.error;
      }
    } catch {
      // body wasn't JSON; keep raw text + undefined detail
    }
    throw new APIError(res.status, msg || res.statusText, detail);
  }
  // 204 No Content / empty bodies
  if (res.status === 204 || res.headers.get("Content-Length") === "0") {
    return undefined as T;
  }
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  delete: <T>(path: string) => request<T>("DELETE", path),
  // v2.6.19 — multipart upload for YouTube cookies replace. Browser
  // auto-sets the Content-Type boundary; we MUST NOT set it manually
  // or fetch drops the boundary and the server fails to parse.
  postForm: async <T>(path: string, form: FormData): Promise<T> => {
    const res = await fetch(path, {
      method: "POST",
      body: form,
      credentials: "include",
    });
    if (!res.ok) {
      const text = await res.text();
      let msg = text;
      try {
        const j = JSON.parse(text);
        if (j.error) msg = j.error;
      } catch {
        // body wasn't JSON; keep raw
      }
      throw new APIError(res.status, msg || res.statusText);
    }
    if (res.status === 204 || res.headers.get("Content-Length") === "0") {
      return undefined as T;
    }
    return (await res.json()) as T;
  },
};

// ---------------------------------------------------------------------------
// Typed endpoint helpers — the UI imports these instead of raw paths.
//
// Field casing note: the backend returns sqlc-generated structs without JSON
// tags, so wire fields land in PascalCase (e.g. `FollowerCount`). Mirroring
// that in TS keeps lookups trivial; a Day 22 cleanup will normalize to
// snake_case on the server.
// ---------------------------------------------------------------------------

export interface HealthResponse {
  status: "ok" | "degraded";
  version: string;
  database: string;
  timestamp: string;
}

export interface LoginResponse {
  actor: string;
  expires_at: string;
}

export interface Page<T> {
  data: T[];
  page: number;
  limit: number;
  next_page?: number;
}

// Verified against the live wire (Day 25 walkthrough):
//   - pgtype.Timestamptz with Valid=true  → bare RFC3339 string
//   - pgtype.Timestamptz with Valid=false → JSON null
//   - pgtype.Numeric  → bare JSON number (or string for large values) | null
// (pgtype.Date isn't currently exposed raw — campaign date fields go through
// custom *string handler structs.)
export type PgTimestamp = string | null;
export type PgDate = string | null;
export type PgNumeric = number | string | null;

export interface Channel {
  ID: number;
  Platform: string;
  PlatformChannelID: string;
  Handle: string | null;
  Name: string | null;
  Description: string | null;
  Url: string | null;
  ContentType: string | null;
  Region: string | null;
  Language: string | null;
  FollowerCount: number | null;
  TotalContentCount: number | null;
  TotalViewCount: number | null;
  LastContentAt: PgTimestamp;
  LastFetchedAt: PgTimestamp;
  QuotedPriceUsd: PgNumeric;
  QuoteType: string | null;
  QuoteDetails: string | null;
  // v2.1.9 — KOL identity (permanent, per-KOL); same code/uid across the
  // KOL's deals. See memory[kol-referral-code-model].
  ReferralCode: string | null;
  KolUid: string | null;
  InternalTier: string | null;
  LifecycleStatus: string;
  TrackingMode: string;
  // v2.8.0 — cross-platform identity FK. Migration 026 enforces
  // NOT NULL at the DB layer via a BEFORE INSERT trigger, so every
  // channel observed by the UI has a real entity_id. Kept as `number`
  // (not `number | null`) to match the wire shape.
  EntityID: number;
  LastScheduledAt: PgTimestamp;
  NextEvaluationAt: PgTimestamp;
  DiscoveredVia: string | null;
  CreatedAt: PgTimestamp;
  UpdatedAt: PgTimestamp;
  // v1.4 CRM fields
  ContactEmail: string | null;
  ContactTelegram: string | null;
  ContactDiscord: string | null;
  ContactAgent: string | null;
  ContactNotes: string | null;
  OutreachStage: string;
  LastContactedAt: PgTimestamp;
  NextFollowupAt: PgTimestamp;
  // Joined from channel_scores by GET /api/channels (list endpoint only;
  // GET /api/channels/:id returns these as undefined). Fetch fresh scoring
  // for a single channel via /api/channels/:id/score.
  EngagementRate?: PgNumeric;
  CompetitorOverlap?: number | null;
  FollowerPct?: PgNumeric;
  ActivityPct?: PgNumeric;
  EngagementPct?: PgNumeric;
  // v2.17.x — quality-weighted composite (0-100). Joined by the list endpoint
  // only; powers the optional "综合分" sort + column. NULL below
  // min_sample_for_percentile or when follower_pct is unknown.
  CompositeScore?: PgNumeric;
  AutoDecisionSuggestion?: string | null;
  // v1.4.1 — number of campaigns this KOL is currently signed to (status
  // in planned/active). Only populated by the channels list endpoint.
  ActiveCampaignCount?: number;
  // v2.0.6 — MEDIAN views per video in the last 90 days (was mean before;
  // mean got distorted by viral outliers on X). Drives 中位播 column +
  // 预估 CPM = quoted_price_usd × 1000 / median. Null/0 when no videos in
  // window or view counts not yet scraped.
  MedianViews90d?: number | null;
}

export interface ChannelScore {
  ChannelID: number;
  ComputedAt: PgTimestamp;
  FollowerCount: number | null;
  Activity30d: number | null;
  EngagementRate: PgNumeric;
  GrowthRate90d: PgNumeric;
  CompetitorOverlap: number | null;
  FollowerPct: PgNumeric;
  ActivityPct: PgNumeric;
  EngagementPct: PgNumeric;
  GrowthPct: PgNumeric;
  CompositeScore: PgNumeric;
  AutoDecisionSuggestion: string | null;
  AutoDecisionReasons: string | null; // base64-encoded JSONB
}

export interface EvaluationTask {
  ID: number;
  ChannelID: number;
  Source: string;
  SourceBatchID: number | null;
  TriggerReason: string | null;
  Priority: string;
  Status: string;
  DecisionID: number | null;
  CreatedAt: PgTimestamp;
  DueBy: PgTimestamp;
  CompletedAt: PgTimestamp;
}

export interface Evaluation {
  ID: number;
  ChannelID: number;
  TaskID: number | null;
  MetricsSnapshot: string; // base64-encoded JSONB
  ScoresSnapshot: string;
  Decision: string;
  DecisionMethod: string | null;
  Rationale: string | null;
  NextReviewAt: PgTimestamp;
  Evaluator: string | null;
  EvaluatedAt: PgTimestamp;
  Tags: string;
}

export interface Video {
  ID: number;
  ChannelID: number;
  PlatformVideoID: string;
  Title: string | null;
  Url: string | null;
  PublishedAt: PgTimestamp;
  ViewCount: number | null;
  LikeCount: number | null;
  CommentCount: number | null;
}

export interface CreateDecisionRequest {
  task_id: number;
  decision: "candidate" | "rejected" | "monitor_only" | "deferred";
  rationale?: string;
  next_review_at?: string; // RFC3339, required for `deferred`
  tags?: string[];
}

export interface CreateDecisionResponse {
  evaluation_id: number;
  channel_id: number;
  task_id: number;
  decision: string;
  new_lifecycle_status: string;
  next_review_at: string;
}

// v2.2.5 — bulk decision request/response. Partial-success: each task
// processed independently, failures don't block others.
export interface BulkDecisionsRequest {
  task_ids: number[];
  decision: "candidate" | "rejected" | "monitor_only" | "deferred";
  rationale?: string;
  next_review_at?: string; // required for deferred
  tags?: string[];
}

export interface BulkDecisionsResponse {
  succeeded: number;
  failed: number;
  results: {
    task_id: number;
    success: boolean;
    error?: string;
    decision?: CreateDecisionResponse;
  }[];
}

// Sponsorships — the list endpoint joins video/channel/exchange columns onto
// each row. Field casing mirrors sqlc's PascalCase output.
export interface SponsorshipRow {
  ID: number;
  VideoID: number;
  ExchangeID: number;
  DetectionMethod: string;
  Confidence: string;
  DetectionSignals: string; // base64 JSONB
  ReviewStatus: string;
  ReviewedBy: string | null;
  ReviewedAt: PgTimestamp;
  ReviewNotes: string | null;
  IsOurCampaign: boolean;
  CampaignID: number | null;
  CreatedAt: PgTimestamp;
  VideoTitle: string | null;
  VideoUrl: string | null;
  VideoPublishedAt?: PgTimestamp;
  ChannelID: number;       // v1.3.1: joined from videos.channel_id
  ChannelName: string | null;
  ChannelPlatform: string;
  ExchangeCode: string;
  ExchangeDisplayName: string;
  // v2.1.4 — how many exchanges were detected in the same video. 1 =
  // dedicated, 2+ = shared (airdrop list / comparison). Metadata only.
  CoOccurringExchangesCount: number;
  // v2.1.6 — numeric confidence 0-1; auto-decided when outside 0.30-0.90.
  ConfidenceScore: number;
  // v2.6 — shadow score with exchange-aware referral_link binding (v2.6.0)
  // + sibling-aware demotion (v2.6.1). When ConfidenceScoreV2 lags
  // ConfidenceScore, v2 disagreed: either the URL belonged to a different
  // exchange or a strong sibling on the same video dominated. v2.6.1 gates
  // auto-confirm on BOTH scores so divergent rows route to pending review.
  // Null on rows detected before v2.6.0 ship.
  ConfidenceScoreV2: number | null;
  // v2.7.4 — rule-engine promotion-style classification:
  //   "content_focused" → alias in title or early description
  //   "link_only"       → alias only deep in description, near a URL
  //                      (affiliate harvest on unrelated content)
  //   "mixed"           → ambiguous (alias buried, no nearby URL)
  // Null on rows detected before v2.7.4 ship.
  PromotionStyle: string | null;
  // v2.7.4 — LLM's independent verdict on the same dimension (shadow
  // column, same pattern as ConfidenceScoreV2). Disagreement with
  // PromotionStyle surfaces as a small flag in the review UI.
  PromotionStyleLlm: string | null;
}

// v2.2.1 — LLM-generated exchange insights (cached payload)
export interface ExchangeInsights {
  exchange_id: number;
  window_days: number;
  // v2.2.2 — these may be empty when no cache exists yet AND generation
  // is still in progress; the UI shows a "正在生成…" state until the
  // poll picks up a real payload.
  generated_at?: string;
  model?: string;
  content_count?: number;
  input_tokens?: number;
  output_tokens?: number;
  insights?: {
    top_topics: { topic: string; count: number; example?: string }[];
    promotion_types: { type: string; count: number }[];
    summary: string;
    notes?: string;
  };
  is_generating: boolean;
  generating_for?: string; // e.g. "12s"
  // v2.2.2.1 — surface the last failed refresh attempt so the UI can
  // explain why the cache is still empty/stale instead of showing a
  // generic "未缓存" message.
  last_error?: string;
  last_error_at?: string;
  // v2.2.2.3 — true when cache is older than insights_cache_hours
  // (default 24h). UI shows a yellow badge prompting manual refresh
  // (the prewarm cron normally keeps this false).
  is_stale?: boolean;
}

// v2.2.2 — async refresh returns 202 immediately; the UI then polls
// exchangeInsights() to pick up the result once is_generating flips
// back to false.
export interface ExchangeInsightsRefreshResponse {
  status: "started" | "already_generating";
  exchange_id: number;
  window_days: number;
  generating_for?: string;
}

// v2.2.4 — DB-backed discovery keyword editor.
export interface DiscoveryKeyword {
  id: number;
  keyword: string;
  enabled: boolean;
  created_at: string;
  created_by?: string;
  last_run_at?: string;
  last_run_result_count?: number;
  last_run_new_channels?: number;
  last_run_error?: string;
}

// v2.2.2 — Gemini key pool dashboard widget data.
export interface LLMPoolStatus {
  date: string;
  total_keys: number;
  healthy_keys: number;
  dead_keys: number;
  keys: LLMPoolKey[];
  model_caps: Record<string, number>;
}

export interface LLMPoolKey {
  suffix: string;
  dead: boolean;
  dead_reason?: string;
  model_usage: Record<string, number>;
  last_success?: string;
  last_429?: string;
}

// v2.2.0 — per-exchange dashboard data
export interface ExchangeAnalytics {
  days: number;
  summary: {
    total_sponsorships: number;
    distinct_videos: number;
    distinct_channels: number;
    youtube_count: number;
    twitter_count: number;
    tiktok_count: number;
    confirmed_count: number;
    pending_count: number;
    rejected_count: number;
    dedicated_count: number;
    shared_count: number;
    total_views: number;
    total_likes: number;
    median_views: number;
    avg_score: number;
  };
  weekly_trend: { week_start: string; sponsorships: number; total_views: number }[];
  top_channels: {
    channel_id: number;
    platform: string;
    handle?: string;
    name?: string;
    follower_count?: number;
    lifecycle_status: string;
    sponsorships: number;
    total_views: number;
    latest_at?: string;
  }[];
}

export interface ExchangeListItem {
  id: number;
  code: string;
  display_name: string;
  tier?: string;
  is_self: boolean;
}

// v2.8.0 — Entities (cross-platform identity). One entity owns N channels
// across platforms (1 Sheldon = his @ on X + YT). The Day 4-7 UI surfaces
// the list / detail / merge UX backed by /api/entities.

export interface EntityListItem {
  id: number;
  canonical_name: string;
  primary_handle?: string;
  is_internal: boolean;
  channels_count: number;
  notes?: string;
  created_at: string;
}

export interface EntityListResponse {
  items: EntityListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface EntityChannelRef {
  id: number;
  platform: string;
  platform_channel_id: string;
  handle?: string;
  name?: string;
  url?: string;
  follower_count?: number;
  lifecycle_status: string;
}

export interface EntityMergeHistoryItem {
  history_id: number;
  channel_id: number;
  channel_name?: string;
  channel_handle?: string;
  channel_platform: string;
  from_entity_id: number;
  from_entity_canonical_name?: string;
  actor: string;
  reason?: string;
  created_at: string;
}

export interface EntityDetail {
  id: number;
  canonical_name: string;
  primary_handle?: string;
  is_internal: boolean;
  notes?: string;
  entity_type?: string;
  country?: string;
  created_at: string;
  updated_at: string;
  channels: EntityChannelRef[];
  merge_history: EntityMergeHistoryItem[];
}

export interface ListEntitiesParams {
  search?: string;
  is_internal?: boolean;
  limit?: number;
  offset?: number;
}

export interface CreateEntityRequest {
  canonical_name: string;
  primary_handle?: string;
  is_internal?: boolean;
  notes?: string;
}

// v2.8.0 wrap — PATCH /api/entities/{id}. Each field is optional; omit
// (or undefined) to leave alone. To clear PrimaryHandle / Notes back to
// NULL, pass an empty string (the backend interprets "" as "set to NULL"
// when the key is present).
export interface UpdateEntityRequest {
  canonical_name?: string;
  primary_handle?: string;
  notes?: string;
  is_internal?: boolean;
}

export interface MoveChannelRequest {
  reason?: string;
}

export interface MoveChannelResponse {
  channel_id: number;
  // v2.8.0 Day 8 — required after migration 026; every channel has an
  // entity_id, so a move always has a non-null from_entity_id.
  from_entity_id: number;
  to_entity_id: number;
  history_id?: number;
  deleted_source_entity_id?: number;
  no_op?: boolean;
}

// v1.4.7 — full admin view including detection patterns. Different
// endpoint (/api/exchanges/admin) from the minimal dropdown list.
export interface ExchangeAdminItem {
  id: number;
  code: string;
  display_name: string;
  tier?: string;
  is_self: boolean;
  aliases: string[];
  domains: string[];
  referral_patterns: string[];
  sponsorship_count: number;
}

// ---- X (Twitter) cookies management ----
//
// XAccountStatus mirrors what the Go API proxies from the twscrape sidecar.
// Active=false means cookies are dead (operator needs to re-login + re-paste).
export interface XAccountStatus {
  username: string;
  active: boolean;
  last_used?: string;
  error_msg?: string;
  total_req: number;
}

export interface XAccountsHealth {
  reachable: boolean;
  account_count: number;
  active_count: number;
  error?: string;
}

export interface AddXAccountRequest {
  username: string;
  auth_token: string;
  ct0: string;
}

export interface XAccountsVerifyResponse {
  ok: boolean;
  verified_via?: string;
  followers?: number;
  message: string;
}

// v2.6.19 — YouTube cookies file status (single-file analog of the X
// pool). Backend returns only metadata; cookie content is never echoed
// back to the UI (file is mode-600, only yt-dlp on the host reads it).
export interface YouTubeCookiesStatus {
  configured: boolean;     // server-side path wired
  file_present: boolean;   // exists + size > 0
  file_size?: number;
  modified_at?: string;    // file mtime UTC RFC3339
  cookie_lines?: number;   // non-blank non-# line count
  probe_alive: boolean;    // latest cron probe verdict
  probe_reason?: string;   // yt-dlp tail when dead
  probe_checked_at?: string;
  // v2.17.2 — why it's dead. An unlabelled red pill reads as "cookies
  // expired" no matter the real cause, which is wrong for vpn_down /
  // bot_challenge / probe_timeout and sends the operator to re-export
  // a healthy cookies file.
  probe_failure_type?: string;
  probe_consecutive_dead?: number;
}

export interface YouTubeCookiesUploadResponse {
  ok: boolean;
  file_size: number;
  probe_alive: boolean;
  probe_error?: string;
  // v2.17.2 — probe_inconclusive means the probe neither confirmed nor
  // refuted the cookies (transient extractor abort / timeout / the
  // canary itself was unavailable). The file IS saved and the stored
  // health state was deliberately left untouched, so the UI must not
  // render this as "cookies dead".
  probe_inconclusive?: boolean;
  probe_failure_type?: string;
  message?: string;
}

export interface ExchangeUpsertRequest {
  // code is only required for create; omitted for update
  code?: string;
  display_name: string;
  tier?: string;
  aliases: string[];
  domains: string[];
  referral_patterns: string[];
}

export interface ChannelSnapshot {
  ChannelID: number;
  CapturedAt: PgTimestamp;
  FollowerCount: number | null;
  TotalContentCount: number | null;
  TotalViewCount: number | null;
  Extras: string | null;
}

export interface ReviewSponsorshipRequest {
  review_status: "confirmed" | "rejected" | "pending";
  review_notes?: string;
  campaign_action?: CampaignActionPayload;
}

// v1.2: when confirming a self (Bitbase) sponsorship, choose one of:
//   - link            : attach to an existing campaign whose channel_id pair
//                       already exists (no new campaign_channels row).
//   - link_channel    : attach to an existing campaign and create a new
//                       campaign_channels row for this KOL inline.
//   - create          : new umbrella + new campaign_channels row.
//   - skip            : confirm without tying to any campaign (organic推广).
export type CampaignActionPayload =
  | { type: "link"; link_campaign_id: number }
  | { type: "link_channel"; link_campaign_id: number; create_campaign: CreateCampaignDraft }
  | { type: "create"; create_campaign: CreateCampaignDraft }
  | { type: "skip" };

export interface CreateCampaignDraft {
  name?: string;
  channel_id: number;
  referral_code?: string;
  kol_uid?: string;
  start_date?: string;
  status?: string;
  notes?: string;
}

// Manual channel/video entry — Day 9 endpoints, surfaced to the UI Day 26
// (post-Phase-1 polish). Lets the operator add channels/videos that the
// scheduler can't or won't auto-discover (manual_static X handles, off-API
// TikTok creators, deliverable records that haven't yet hit detection).
// Subset of channel fields the operator can edit through PATCH
// /api/channels/{id}. Each is optional: undefined means "leave alone".
// Empty string is treated the same as undefined (no clear-to-null in v1).
export interface UpdateChannelRequest {
  content_type?: string;
  region?: string;
  language?: string;
  internal_tier?: string;
  quoted_price_usd?: number;
  quote_type?: string;
  quote_details?: string;
  referral_code?: string;
  kol_uid?: string;
}

export interface ManualCreateChannelRequest {
  // DB stores X as "twitter" (legacy from Phase 1); operator-facing label is "X".
  // v2.9.0 added "rss" (Medium / Substack / generic feeds), "foresight"
  // (Foresight News columns via WAF-bypass sidecar), and "panews"
  // (panewslab.com columns via the same sidecar).
  platform: "youtube" | "twitter" | "tiktok" | "rss" | "foresight" | "panews";
  url_or_handle: string;
  name: string;
  description?: string;
  follower_count?: number;
  total_content_count?: number;
  total_view_count?: number;
  content_type?: string;
  region?: string;
  language?: string;
}

export interface ManualChannelResponse {
  id: number;
  platform: string;
  platform_channel_id: string;
  handle?: string;
  name?: string;
  follower_count?: number;
  tracking_mode: string;
  lifecycle_status: string;
  discovered_via?: string;
}

export interface DeliverableRequest {
  channel_id: number;
  video_url: string;
  title?: string;
  description?: string;
  published_at: string; // RFC3339
  view_count?: number;
  like_count?: number;
  comment_count?: number;
}

export interface DeliverableResponse {
  campaign_id: number;
  video_id: number;
  sponsorship_id: number;
}

// One deliverable video as listed on the campaign detail page. Joins
// sponsorship (campaign_id=X) → video. video_channel_id lets the UI group
// deliverables under each campaign_channels card.
// v2.6.11 — third-party threat-intel verdict cached per registrable
// domain. Drives the "⚠️ phishing_confirmed" badge on suspicious_proxy
// signals in SponsorshipReview. Severity computed server-side from
// VirusTotal + Safe Browsing.
export interface DomainReputationItem {
  domain: string;
  vt_malicious?: number;
  vt_suspicious?: number;
  vt_harmless?: number;
  vt_undetected?: number;
  vt_total?: number;
  vt_scanned_at?: string;
  sb_threat_types?: string[];
  sb_scanned_at?: string;
  last_scanned_at: string;
  severity: "phishing_confirmed" | "phishing_suspected" | "clean" | "";
  // v2.6.13 — operator-supplied verdict that overrides VT/SB.
  manual_verdict?: "phishing" | "clean" | "";
  manual_by?: string;
  manual_reason?: string;
  manual_at?: string;
}

export interface DeliverableItem {
  sponsorship_id: number;
  video_id: number;
  video_channel_id: number;
  title?: string;
  url?: string;
  published_at?: string;
  review_status: string;
  confidence: string;
  view_count?: number;
  like_count?: number;
  comment_count?: number;
  // v2.5 — Drive archive status (BB-campaign deliverables only).
  archive_drive_url?: string;
  archive_error?: string;
  archived_at?: string;
}

export interface ArchiveVideoResponse {
  archived: boolean;
  drive_url?: string;
  drive_file_id?: string;
  size_bytes?: number;
  error?: string;
}

// 24H / 72H / 7D milestone snapshot for a single video. offset_hours = 0
// is "current" (latest sample). Others map to the requested milestones.
export interface MilestoneSnapshot {
  offset_hours: number;
  captured_at: string;
  view_count?: number;
  like_count?: number;
  comment_count?: number;
  share_count?: number;
}

// Discovery — KOLs already promoting competitor exchanges. Surfaced by
// /api/discovery/competitor-partners. Rows where lifecycle_status is
// untracked/discovered are the high-value "new KOL" leads; rows already
// in evaluating/candidate are validation that the KOL works with crypto
// brands and worth pursuing.
// Per-exchange breakdown of one channel's competitor推广 history. Returned
// by GET /api/channels/:id/competitor-breakdown. Drives the click-to-detail
// popover on the channels table's "竞品重叠" cell.
export interface CompetitorBreakdownItem {
  exchange_id: number;
  code: string;
  display_name: string;
  video_count: number;
  latest_video_at?: string;
}

// v1.3.5 — per-channel drill-down: every competitor推广 video this KOL
// posted in the window. Used by ChannelDetail's "竞品推广记录" section.
export interface CompetitorSponsorshipItem {
  sponsorship_id: number;
  video_id: number;
  video_title?: string;
  video_url?: string;
  video_published_at?: string;
  video_view_count?: number;
  video_like_count?: number;
  exchange_id: number;
  exchange_code: string;
  exchange_display_name: string;
  review_status: string;
  confidence: string;
  detection_method: string;
  detected_at?: string;
  // v2.1.4 — how many distinct exchanges were detected in the same content.
  // 1 = dedicated, 2+ = shared (airdrop list / comparison post). Metadata
  // only; does not influence confidence or scoring weights.
  co_occurring_exchanges: number;
  // v2.1.6 — numeric confidence 0-1; >=0.90 was auto-confirmed.
  confidence_score: number;
}

export interface CompetitorPartner {
  channel_id: number;
  platform: string;
  handle?: string;
  name?: string;
  follower_count?: number;
  lifecycle_status: string;
  total_sponsorships: number;
  confirmed_count: number;
  distinct_competitors: number;
  latest_video_at?: string;
  competitors: string[];
}

// v2.8.0 Day 9 — entity-grain variant of CompetitorPartner. The two are
// served by sibling endpoints with the same filter set; the Discovery
// page toggles between them so operators can compare same-window data
// at channel grain vs cross-platform entity grain.
export interface CompetitorPartnerEntity {
  entity_id: number;
  canonical_name: string;
  primary_handle?: string;
  is_internal: boolean;
  channel_count: number;
  platforms: string[];
  max_follower_count?: number;
  total_sponsorships: number;
  confirmed_count: number;
  distinct_competitors: number;
  latest_video_at?: string;
  competitors: string[];
}

// v1.2 — campaigns is the umbrella; each KOL deal is a campaign_channels
// row. The umbrella response never carries plaintext deal_amount; each
// channel row reports has_deal_amount: bool.
export interface CampaignResponse {
  id: number;
  name: string;
  start_date?: string;
  end_date?: string;
  status: string;
  notes?: string;
  channel_count: number;
  deliverable_count: number;
  channels?: CampaignChannelResponse[]; // populated on detail (GET /:id) only
  // v1.4.1: total spend by currency, only on detail. Multi-currency campaigns
  // get split (no FX conversion).
  total_spend_by_currency?: Record<string, number>;
  // v2.7.1: operator-generated LLM executive summary for the recap report.
  // Refresh by POST /api/campaigns/:id/takeaway.
  llm_takeaway?: string;
  llm_takeaway_generated_at?: string;
}

export interface CampaignChannelResponse {
  id: number;
  campaign_id: number;
  channel_id: number;
  channel_name?: string;
  channel_handle?: string;
  channel_platform?: string;
  channel_follower_count?: number;
  referral_code?: string;
  kol_uid?: string;
  agreed_deliverables?: string;
  payment_currency?: string;
  notes?: string;
  has_deal_amount: boolean;
  // v1.4.1: decrypted plaintext amount, only on GET /campaigns/:id
  deal_amount?: number;
}

export interface CampaignChannelDraft {
  channel_id: number;
  referral_code?: string;
  kol_uid?: string;
  agreed_deliverables?: string;
  deal_amount?: number;
  payment_currency?: string;
  notes?: string;
}

export interface CreateCampaignRequest {
  name: string;
  start_date?: string;
  end_date?: string;
  status?: string;
  notes?: string;
  channels: CampaignChannelDraft[];
}

export interface UpdateCampaignRequest {
  name?: string;
  start_date?: string;
  end_date?: string;
  status?: string;
  notes?: string;
}

// Drill-down from a channel page → which campaigns has this KOL run.
export interface CampaignChannelByChannelItem {
  campaign_id: number;
  campaign_name: string;
  campaign_status: string;
  campaign_start_date?: string;
  campaign_end_date?: string;
  campaign_channel_id: number;
  referral_code?: string;
  kol_uid?: string;
  agreed_deliverables?: string;
  payment_currency?: string;
  has_deal_amount: boolean;
  deal_amount?: number;  // v1.4.1 — decrypted on the channel detail endpoint
}

// CSV import — POST /upload's response is immediate ("queued"); the batch
// row is created asynchronously, so the client must follow up with
// GET /api/csv/batches?by_file_hash=<sha256(body)>.
export interface UploadResponse {
  batch_id: number;
  status: string;
  total_rows: number;
  global_error?: string;
  parse_warnings?: string[];
  status_url: string;
  unmatched_headers?: string[];
}

export interface BatchResponse {
  id: number;
  name?: string;
  source?: string;
  uploaded_by?: string;
  file_name?: string;
  total_items: number;
  processed_items: number;
  failed_items: number;
  status: string;
  error_summary?: string;
}

// System status — aggregate counts for dashboard + status page.
export interface SystemStats {
  channels_by_lifecycle: Record<string, number>;
  tasks_by_status: Record<string, number>;
  sponsorships_by_review: Record<string, number>;
  campaigns_by_status: Record<string, number>;
}

export interface QuotaEntry {
  platform: string;
  remaining: number;
  checked_at: string;
}

// Circuit breaker state — one per guarded dependency (e.g. the curl_cffi
// sidecar). Lets the operator tell "we tripped the breaker" apart from "the
// dependency is dead". state: "closed" healthy | "open" tripped | "half-open"
// probing recovery.
export interface BreakerEntry {
  name: string;
  state: "closed" | "open" | "half-open";
  consec_failures: number;
  trips: number;
  cooldown_remaining_sec?: number;
  last_transition?: string;
}

// Scheduler status — one row per registered cron job. last_run_at / next_run_at
// are RFC3339; empty string when the job has never run or is disabled.
export interface SchedulerJobStatus {
  name: string;
  description?: string;
  stage: number;          // 0 = independent, 1..6 = pipeline position
  stage_label?: string;
  schedule: string;
  enabled: boolean;
  optional: boolean;
  last_run_at?: string;
  last_duration_ms?: number;
  last_status?: "ok" | "failed" | "";
  last_error?: string;
  next_run_at?: string;
  is_running: boolean;
}

export interface AuditEntry {
  ID: number;
  Timestamp: string; // RFC3339
  Actor: string | null;
  Action: string;
  EntityType: string | null;
  EntityID: number | null;
  // v2.2.4 — Details is now raw JSON (object/array/value) thanks to
  // backend re-marshal as json.RawMessage. UI gets it as unknown and
  // pretty-prints via JSON.stringify.
  Details: unknown;
}

// v2.2.4 — audit search filter params. All optional.
export interface AuditSearchFilters {
  actor?: string;
  action?: string; // prefix match
  entity_type?: string;
  entity_id?: number;
  since?: string; // RFC3339
  until?: string; // RFC3339
  details?: string; // substring search on JSONB text
}

export interface BatchItem {
  ID: number;
  BatchID: number;
  LineNumber: number | null;
  RawInput: string; // base64 JSONB
  ResolvedUrl: string | null;
  ChannelID: number | null;
  Status: string;
  ErrorMessage: string | null;
  CreatedAt: PgTimestamp;
  CompletedAt: PgTimestamp;
}

// Coerce a pgtype.Numeric wire value (bare JSON number or string) to a JS
// number. Returns null for null/NaN/non-numeric.
export function numericToNumber(n: PgNumeric | null | undefined): number | null {
  if (n === null || n === undefined) return null;
  const v = typeof n === "number" ? n : Number(n);
  return Number.isFinite(v) ? v : null;
}

interface CampaignListParams {
  status?: string;
  page?: number;
  limit?: number;
}

interface SponsorshipListParams {
  exchange_id?: number;
  review_status?: string;
  // v2.7.4 — promotion_style filter. Accepts a literal style value
  // ("content_focused" / "link_only" / "mixed") for exact match, or
  // the special "not_link_only" to exclude noise (UI default).
  promotion_style?: string;
  page?: number;
  limit?: number;
}

interface ChannelListParams {
  lifecycle?: string;
  platform?: string;
  content_type?: string;
  region?: string;
  include_archived?: boolean;
  // v2.17.x — 'composite' → quality-weighted 综合分 ordering; '' / 'followers'
  // (default) → follower_count DESC.
  sort?: string;
  page?: number;
  limit?: number;
}

export type ChannelLifecycle =
  | "discovered"
  | "evaluating"
  | "candidate"
  | "active"
  | "monitor_only"
  | "rejected"
  | "archived"
  | "blacklisted"; // v2.6.13 — never deal with this KOL again, auto-reject pending

export interface UpdateChannelLifecycleRequest {
  lifecycle_status: ChannelLifecycle;
  next_review_at?: string; // RFC3339
}

export type OutreachStage =
  | "idle"
  | "reaching_out"
  | "negotiating"
  | "committed"
  | "dropped";

// OUTREACH_STAGE_META is the single source of truth for outreach-stage display
// (label + badge variant + order). Typed as Record<OutreachStage, ...> so TS
// rejects adding a stage to the union without adding it here (and vice versa).
// InlineEditCell's option list and ChannelCRMSection's badge map both derive
// from this, so the UI surfaces can't drift. Mirror the value set in
// internal/web/api/channels.go (outreachStages); a Go test pins that list.
export const OUTREACH_STAGE_META: Record<
  OutreachStage,
  { label: string; variant: "secondary" | "info" | "warning" | "success" | "danger"; order: number }
> = {
  idle: { label: "未开始", variant: "secondary", order: 0 },
  reaching_out: { label: "已联络", variant: "info", order: 1 },
  negotiating: { label: "谈判中", variant: "warning", order: 2 },
  committed: { label: "已成交", variant: "success", order: 3 },
  dropped: { label: "已放弃", variant: "danger", order: 4 },
};

// Outreach stages in display order, derived from OUTREACH_STAGE_META.
export const OUTREACH_STAGES_ORDERED: OutreachStage[] = (
  Object.keys(OUTREACH_STAGE_META) as OutreachStage[]
).sort((a, b) => OUTREACH_STAGE_META[a].order - OUTREACH_STAGE_META[b].order);

export interface UpdateChannelContactRequest {
  contact_email?: string;
  contact_telegram?: string;
  contact_discord?: string;
  contact_agent?: string;
  contact_notes?: string;
  outreach_stage?: OutreachStage;
  next_followup_at?: string; // RFC3339; empty string clears
}

interface TaskListParams {
  status?: string;
  priority?: string;
  // v2.3.5 — source filter. Values: 'csv_import' | 'manual' | 'manual_add'
  // | 're_evaluation' | 'keyword_discovery'. Empty = no filter.
  source?: string;
  page?: number;
  limit?: number;
}

function qs(params: Record<string, unknown>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === "" || v === null) continue;
    q.set(k, String(v));
  }
  return q.toString();
}

// v2.4.0 — CSV export URL builder. UI components use this to construct a
// download URL for the current view's filters; rendering it as an <a
// download> or window.open triggers the browser's native download flow
// (cookies are sent automatically since it's same-origin). NOT a fetch —
// JSON-parsing a CSV response would explode.
export function csvDownloadURL(
  path: string,
  filters: Record<string, unknown> = {},
): string {
  const q = qs({ ...filters, format: "csv" });
  return q ? `${path}?${q}` : `${path}?format=csv`;
}

// ---------------------------------------------------------------------------
// Investment Plans (v2.12 投放工作台)
// ---------------------------------------------------------------------------

export type PlanStatus = "draft" | "promoted" | "archived";
export type PlanCandidateTier = "perception" | "core" | "secondary" | null;
export type PlanCandidateEnrichmentState = "pending" | "enriched" | "failed";

export interface PlanSummary {
  id: number;
  name: string;
  platform: string;
  status: PlanStatus;
  budget_usd?: number | null;
  funnel_constants: unknown; // JSONB — opaque on the list page
  notes: string | null;
  campaign_id: number | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  candidate_count: number;
  selected_count: number;
  pending_enrichment_count: number;
}

// Risk flag stored under candidate.RiskFlags (planning.RiskFlag JSON shape).
export interface PlanRiskFlag {
  type: string;
  level: "red" | "yellow" | "info";
  detail: string;
}

// ListPlanCandidatesEnrichedRow PascalCase shape from the backend.
// Heavy join — many fields. We only type the ones the UI actually reads;
// extras come through as unknown but don't break.
export interface PlanCandidate {
  CandidateID: number;
  PlanID: number;
  ID: number; // channel id
  Platform: string;
  PlatformChannelID: string;
  Handle: string | null;
  Name: string | null;
  Url: string | null;
  FollowerCount: number | null;
  ContentType: string | null;
  Region: string | null;
  Language: string | null;
  LifecycleStatus: string;
  // Candidate-row fields:
  QuotedPriceUsd: PgNumeric;
  Tier: string | null;
  TierOverride: boolean;
  Themes: PlanThemeSuggestion[] | null;
  PromoRatio: PgNumeric;
  Score: PgNumeric;
  RiskFlags: PlanRiskFlag[] | null;
  IncludedInFinal: boolean;
  CandidateNotes: string | null;
  EnrichmentState: PlanCandidateEnrichmentState;
  CandidateCreatedAt: PgTimestamp;
  CandidateUpdatedAt: PgTimestamp;
  // From channel_scores join:
  EngagementRate: PgNumeric;
  MedianViews90d: number;
  // From correlated subqueries:
  CompetitorOverlap60d: number;
  PriorPlanCount: number;
  LastPromotedAt: PgTimestamp;
}

export interface PlanThemeSuggestion {
  key: string;
  confidence?: number;
}

export interface PlanDetailResponse {
  plan: PlanSummary;
  candidates: PlanCandidate[];
}

export interface CreatePlanRequest {
  name: string;
  platform: string;
  budget_usd?: number | null;
  notes?: string;
}

export interface UpdatePlanRequest {
  name?: string;
  budget_usd?: number | null;
  notes?: string | null;
  funnel_constants?: unknown;
}

export interface PlanImportLineResult {
  input: string;
  status: "existing" | "new" | "duplicate" | "skipped_rejected" | "error";
  channel_id?: number;
  platform_channel_id?: string;
  reason?: string;
  // v2.12 hotfix — inline FetchChannel result for new channels.
  fetched?: boolean;
  fetch_failed?: boolean;
}

export interface PlanImportResponse {
  existing: number;
  new: number;
  duplicates: number;
  errors: number;
  skipped_rejected: number;
  fetched: number;
  fetch_failed: number;
  lines: PlanImportLineResult[];
}

export interface UpdatePlanCandidateRequest {
  quoted_price_usd?: number | null;
  tier?: string; // "" clears the override
  included_in_final?: boolean;
  notes?: string;
}

export interface PromotePlanRequest {
  campaign_name?: string;
  campaign_status?: string;
  start_date?: string;
  end_date?: string;
}

export interface PromotePlanResponse {
  campaign_id: number;
  promoted_channel_ids: number[];
}

export const endpoints = {
  health: () => api.get<HealthResponse>("/api/health"),
  login: (token: string) => api.post<LoginResponse>("/api/auth/login", { token }),
  logout: () => api.post<void>("/api/auth/logout"),

  // ---- Channels ----
  channels: (params: ChannelListParams = {}) =>
    api.get<Page<Channel>>(`/api/channels?${qs({ ...params })}`),
  channel: (id: number) => api.get<Channel>(`/api/channels/${id}`),
  updateChannel: (id: number, body: UpdateChannelRequest) =>
    api.patch<Channel>(`/api/channels/${id}`, body),
  updateChannelLifecycle: (id: number, body: UpdateChannelLifecycleRequest) =>
    api.patch<Channel>(`/api/channels/${id}/lifecycle`, body),
  updateChannelContact: (id: number, body: UpdateChannelContactRequest) =>
    api.patch<Channel>(`/api/channels/${id}/contact`, body),
  reevaluateChannel: (id: number) =>
    api.post<EvaluationTask>(`/api/channels/${id}/reevaluate`),
  channelIDs: (params: ChannelListParams = {}) =>
    api.get<{ ids: number[]; count: number; truncated: boolean }>(
      `/api/channels/ids?${qs({ ...params })}`,
    ),
  bulkChannelLifecycle: (ids: number[], lifecycle: ChannelLifecycle) =>
    api.post<{ updated: number; status: string }>("/api/channels/bulk/lifecycle", {
      ids,
      lifecycle_status: lifecycle,
    }),
  bulkChannelReevaluate: (ids: number[]) =>
    api.post<{ queued: number[]; skipped: number[]; failed?: number[] }>(
      "/api/channels/bulk/reevaluate",
      { ids },
    ),
  // v1.4.5 — operator-triggered, scope-narrowed refresh. Different from
  // /system manual cron runs (which do all eligible channels): these run
  // ONLY on the supplied IDs. Sync — wait for response.
  // v2.3.2 — incremental flag (default true) gates whether the backend uses
  // last_content_at as a watermark. Hot channels typically pull 0-2 new
  // videos in incremental mode vs ~90 days of re-fetch in backfill mode.
  // Pass `incremental: false` only when the operator explicitly wants to
  // backfill history (e.g. newly added KOL, corrupted last_content_at).
  refreshChannels: (
    ids: number[],
    opts?: { lookbackDays?: number; incremental?: boolean },
  ) =>
    api.post<{
      results: Array<{
        channel_id: number;
        ok: boolean;
        follower_count?: number;
        videos_upserted?: number;
        stats_refreshed?: number;
        error?: string;
      }>;
      detection_ran: boolean;
      detection_videos_scanned?: number;
      detection_sponsorships?: number;
      detection_error?: string;
    }>("/api/channels/refresh", {
      channel_ids: ids,
      ...(opts?.lookbackDays ? { lookback_days: opts.lookbackDays } : {}),
      ...(opts?.incremental === false ? { incremental: false } : {}),
    }),
  refreshCampaignDeliverables: (campaignID: number) =>
    api.post<{
      videos_considered: number;
      videos_refreshed: number;
      snapshots_written: number;
      failed?: number;
    }>(`/api/campaigns/${campaignID}/refresh-deliverables`),
  // v1.4.8 — re-scan historical videos with current detection rules.
  // Use case: just added a new exchange / updated lexicon, want to detect
  // sponsorships on already-processed videos.
  redetectSponsorships: (days?: number) =>
    api.post<{
      videos_reset: number;
      detection_ran: boolean;
      videos_scanned?: number;
      sponsorships_count?: number;
      sponsorships_new?: number;          // v1.4.9 — net new inserts
      sponsorships_reconfirmed?: number;  // v1.4.9 — already-known rows re-emitted
      detection_error?: string;
    }>("/api/sponsorships/redetect", days ? { days } : {}),
  channelScore: (id: number) => api.get<ChannelScore>(`/api/channels/${id}/score`),
  channelVideos: (id: number, limit = 10) =>
    api.get<Page<Video>>(`/api/channels/${id}/videos?${qs({ limit })}`),
  channelEvaluations: (id: number, limit = 5) =>
    api.get<Page<Evaluation>>(`/api/channels/${id}/evaluations?${qs({ limit })}`),
  channelSnapshots: (id: number, days = 90) =>
    api.get<ChannelSnapshot[]>(`/api/channels/${id}/snapshots?${qs({ days })}`),
  channelCompetitorBreakdown: (id: number, days = 90, includePending = false) =>
    api.get<CompetitorBreakdownItem[]>(
      `/api/channels/${id}/competitor-breakdown?${qs({ days, include_pending: includePending })}`,
    ),
  channelCompetitorSponsorships: (id: number, days = 90, includePending = false) =>
    api.get<CompetitorSponsorshipItem[]>(
      `/api/channels/${id}/competitor-sponsorships?${qs({ days, include_pending: includePending })}`,
    ),

  // ---- Sponsorships ----
  sponsorships: (params: SponsorshipListParams = {}) =>
    api.get<Page<SponsorshipRow>>(`/api/sponsorships?${qs({ ...params })}`),
  reviewSponsorship: (id: number, body: ReviewSponsorshipRequest) =>
    api.post<unknown>(`/api/sponsorships/${id}/review`, body),
  // v2.1.8 — bulk confirm/reject. ids = sponsorship row IDs.
  bulkReviewSponsorships: (body: {
    ids: number[];
    review_status: "confirmed" | "rejected";
    review_notes?: string;
  }) =>
    api.post<{ affected: number; review_status: string }>(
      "/api/sponsorships/bulk-review",
      body,
    ),

  // ---- X (Twitter) cookies management ----
  // Cookies live in the twscrape sidecar's local SQLite. The Go API proxies
  // to it; the UI just paints status + collects new cookie pastes.
  xAccounts: () => api.get<XAccountStatus[]>("/api/x-accounts"),
  xAccountsHealth: () => api.get<XAccountsHealth>("/api/x-accounts/health"),
  addXAccount: (body: AddXAccountRequest) =>
    api.post<{ username: string; active: boolean }>("/api/x-accounts", body),
  deleteXAccount: (username: string) =>
    api.delete<void>(`/api/x-accounts/${encodeURIComponent(username)}`),
  verifyXAccounts: () =>
    api.post<XAccountsVerifyResponse>("/api/x-accounts/verify", {}),

  // ---- YouTube cookies management (v2.6.19) ----
  // Single-file analog of X cookies: status + replace + clear. Backend
  // writes /opt/bitbase-kol/secrets/youtube_cookies.txt atomically and
  // immediately probes via yt-dlp so the upload response carries the
  // alive/dead verdict.
  youtubeCookiesStatus: () =>
    api.get<YouTubeCookiesStatus>("/api/youtube-cookies"),
  youtubeCookiesReplace: (file: File) => {
    const fd = new FormData();
    fd.append("cookies", file);
    return api.postForm<YouTubeCookiesUploadResponse>("/api/youtube-cookies", fd);
  },
  // v2.6.20 — paste-text mode. Simpler than multipart for the operator
  // who just copies cookies from the browser extension's textarea.
  youtubeCookiesReplaceText: (text: string) =>
    api.post<YouTubeCookiesUploadResponse>("/api/youtube-cookies/text", { text }),
  youtubeCookiesClear: () =>
    api.delete<void>("/api/youtube-cookies"),

  // ---- Exchanges (small fixed list, no pagination) ----
  exchanges: () => api.get<ExchangeListItem[]>("/api/exchanges"),
  // v2.2.0 — per-exchange dashboard (sponsorship volume + platform mix +
  // exposure + top KOLs + weekly trend). Default 90 days, top=10.
  exchangeAnalytics: (id: number, days = 90, top = 10) =>
    api.get<ExchangeAnalytics>(
      `/api/exchanges/${id}/analytics?${qs({ days, top })}`,
    ),
  // v2.2.1 — LLM-driven exchange insights (cached). GET returns 404 if
  // never generated for this window; POST /refresh forces a fresh run.
  exchangeInsights: (id: number, days = 90) =>
    api.get<ExchangeInsights | null>(
      `/api/exchanges/${id}/insights?${qs({ days })}`,
    ).catch((e) => {
      if (e instanceof APIError && e.status === 404) return null;
      throw e;
    }),
  refreshExchangeInsights: (id: number, days = 90) =>
    api.post<ExchangeInsightsRefreshResponse>(`/api/exchanges/${id}/insights/refresh?${qs({ days })}`),
  // v2.2.2 — Gemini key pool status (dashboard widget). Returns 503 when
  // LLM is disabled; widget should handle null gracefully.
  llmPoolStatus: () =>
    api.get<LLMPoolStatus | null>("/api/dashboard/llm-pool").catch((e) => {
      if (e instanceof APIError && e.status === 503) return null;
      throw e;
    }),
  // v2.2.5 — probe a candidate key against the pool's upstream (CF
  // gateway included). Backend does NOT persist the key; success
  // response includes copy-paste rotation guidance.
  llmPoolKeyTest: (apiKey: string) =>
    api.post<{
      ok: boolean;
      model: string;
      tokens_in: number;
      tokens_out: number;
      echoed_suffix: string;
      reply_excerpt: string;
    }>("/api/dashboard/llm-pool/keys/test", { api_key: apiKey }),
  exchangesAdmin: () => api.get<ExchangeAdminItem[]>("/api/exchanges/admin"),
  createExchange: (body: ExchangeUpsertRequest) =>
    api.post<ExchangeAdminItem>("/api/exchanges", body),
  updateExchange: (id: number, body: ExchangeUpsertRequest) =>
    api.patch<ExchangeAdminItem>(`/api/exchanges/${id}`, body),
  deleteExchange: (id: number) =>
    api.delete<void>(`/api/exchanges/${id}`),

  // ---- Evaluation tasks + decisions ----
  evaluationTasks: (statusOrParams?: string | TaskListParams) => {
    const p: TaskListParams = typeof statusOrParams === "string"
      ? { status: statusOrParams }
      : statusOrParams ?? {};
    return api.get<Page<EvaluationTask>>(`/api/evaluation-tasks?${qs({ ...p })}`);
  },
  evaluationTask: (id: number) => api.get<EvaluationTask>(`/api/evaluation-tasks/${id}`),
  createDecision: (body: CreateDecisionRequest) =>
    api.post<CreateDecisionResponse>("/api/evaluations", body),
  // v2.2.5 — bulk decision endpoint. Applies the same decision + optional
  // shared rationale/tags to up to 200 tasks per call. Partial-success
  // friendly (each task tried independently).
  bulkCreateDecisions: (body: BulkDecisionsRequest) =>
    api.post<BulkDecisionsResponse>("/api/evaluations/bulk", body),

  // ---- Campaigns ----
  campaigns: (params: CampaignListParams = {}) =>
    api.get<Page<CampaignResponse>>(`/api/campaigns?${qs({ ...params })}`),
  campaign: (id: number) => api.get<CampaignResponse>(`/api/campaigns/${id}`),
  campaignsByChannel: (channelID: number) =>
    api.get<CampaignChannelByChannelItem[]>(`/api/channels/${channelID}/campaigns`),
  createCampaign: (body: CreateCampaignRequest) =>
    api.post<CampaignResponse>("/api/campaigns", body),
  updateCampaign: (id: number, body: UpdateCampaignRequest) =>
    api.patch<CampaignResponse>(`/api/campaigns/${id}`, body),
  deleteCampaign: (id: number) =>
    api.delete<void>(`/api/campaigns/${id}`),
  addCampaignChannel: (campaignID: number, body: CampaignChannelDraft) =>
    api.post<CampaignChannelResponse>(`/api/campaigns/${campaignID}/channels`, body),
  bulkAddCampaignChannels: (campaignID: number, channels: CampaignChannelDraft[]) =>
    api.post<{ added: number[]; skipped: number[]; failed?: number[] }>(
      `/api/campaigns/${campaignID}/channels/bulk`,
      { channels },
    ),
  updateCampaignChannel: (campaignID: number, ccid: number, body: CampaignChannelDraft) =>
    api.patch<CampaignChannelResponse>(`/api/campaigns/${campaignID}/channels/${ccid}`, body),
  deleteCampaignChannel: (campaignID: number, ccid: number) =>
    api.delete<void>(`/api/campaigns/${campaignID}/channels/${ccid}`),
  recordDeliverable: (campaignID: number, body: DeliverableRequest) =>
    api.post<DeliverableResponse>(`/api/campaigns/${campaignID}/deliverables`, body),
  campaignDeliverables: (campaignID: number) =>
    api.get<DeliverableItem[]>(`/api/campaigns/${campaignID}/deliverables`),
  generateCampaignTakeaway: (campaignID: number) =>
    api.post<{ llm_takeaway: string; llm_takeaway_generated_at: string }>(
      `/api/campaigns/${campaignID}/takeaway`,
      {},
    ),
  domainReputation: () =>
    api.get<DomainReputationItem[]>(`/api/domain-reputation`),
  setDomainManualVerdict: (
    domain: string,
    verdict: "phishing" | "clean" | "",
    reason: string,
  ) =>
    api.post<void>(`/api/domain-reputation/manual-verdict`, {
      domain,
      verdict,
      reason,
    }),
  videoMilestones: (videoID: number, at: number[] = [24, 72, 168]) =>
    api.get<MilestoneSnapshot[]>(
      `/api/videos/${videoID}/milestone-stats?${qs({ at: at.join(",") })}`,
    ),
  // v2.5 — synchronously download + upload one video to the Drive
  // archive folder. Blocks ~10s; UI shows pending state. Idempotent —
  // re-calling after success returns the existing drive_url.
  archiveVideo: (videoID: number) =>
    api.post<ArchiveVideoResponse>(`/api/videos/${videoID}/archive`, {}),

  // ---- Manual entry (operator-driven channel / video records) ----
  createManualChannel: (body: ManualCreateChannelRequest) =>
    api.post<ManualChannelResponse>("/api/manual/channels", body),

  // ---- Discovery ----
  competitorPartners: (days = 90, limit = 100) =>
    api.get<CompetitorPartner[]>(
      `/api/discovery/competitor-partners?${qs({ days, limit })}`,
    ),
  // v2.8.0 Day 9 — entity-grain twin. Same filter knobs (days + limit),
  // different aggregation. UI's "Entity 维度" toggle calls this.
  competitorPartnerEntities: (days = 90, limit = 100) =>
    api.get<CompetitorPartnerEntity[]>(
      `/api/discovery/competitor-partner-entities?${qs({ days, limit })}`,
    ),
  // ---- Entities (v2.8.0) ----
  listEntities: (params: ListEntitiesParams = {}) =>
    api.get<EntityListResponse>(`/api/entities?${qs({ ...params })}`),
  entity: (id: number) => api.get<EntityDetail>(`/api/entities/${id}`),
  // v2.8.0 Day 7 — resolves the Bitbase-self singleton. Returns EntityDetail
  // on 200; 404 when no internal entity exists yet (UI shows seed hint);
  // 409 when multiple internal entities (UI shows consolidate hint).
  selfEntity: () => api.get<EntityDetail>("/api/entities/self"),
  createEntity: (body: CreateEntityRequest) =>
    api.post<EntityDetail>("/api/entities", body),
  // v2.8.0 wrap — operator edit. Returns the full EntityDetail shape
  // (channels + merge_history) so the UI can drop straight into its
  // panel state without re-fetching.
  updateEntity: (id: number, body: UpdateEntityRequest) =>
    api.patch<EntityDetail>(`/api/entities/${id}`, body),
  moveChannelToEntity: (channelID: number, toEntityID: number, body: MoveChannelRequest = {}) =>
    api.post<MoveChannelResponse>(
      `/api/channels/${channelID}/move-to-entity/${toEntityID}`,
      body,
    ),

  // v2.2.4 — keyword editor. Backed by discovery_keywords table; the
  // keyword_discovery cron reads from the same source so UI changes
  // take effect on the next 04:00 tick without redeploy.
  listDiscoveryKeywords: () =>
    api.get<DiscoveryKeyword[]>("/api/discovery/keywords"),
  createDiscoveryKeyword: (keyword: string) =>
    api.post<DiscoveryKeyword>("/api/discovery/keywords", { keyword }),
  toggleDiscoveryKeyword: (id: number, enabled: boolean) =>
    api.patch<DiscoveryKeyword>(`/api/discovery/keywords/${id}`, { enabled }),
  deleteDiscoveryKeyword: (id: number) =>
    api.delete<void>(`/api/discovery/keywords/${id}`),

  // ---- CSV import ----
  // upload posts the raw CSV body (not multipart). Optional `?name=...&filename=...`
  // populate the evaluation_batches row. Returns 202 + status_url.
  csvUpload: async (
    body: string,
    opts: { name?: string; filename?: string } = {},
  ): Promise<UploadResponse> => {
    const q = qs({ name: opts.name, filename: opts.filename });
    const res = await fetch(`/api/csv/upload${q ? `?${q}` : ""}`, {
      method: "POST",
      headers: { "Content-Type": "text/csv" },
      body,
      credentials: "include",
    });
    if (!res.ok) {
      const text = await res.text();
      let msg = text;
      try { const j = JSON.parse(text); if (j.error) msg = j.error; } catch {
        // body wasn't JSON; keep raw
      }
      throw new APIError(res.status, msg || res.statusText);
    }
    return (await res.json()) as UploadResponse;
  },
  csvBatchByHash: (hash: string) =>
    api.get<BatchResponse | { batch: null }>(
      `/api/csv/batches?${qs({ by_file_hash: hash })}`,
    ),
  csvBatch: (id: number) => api.get<BatchResponse>(`/api/csv/batches/${id}`),
  csvBatchItems: (id: number) => api.get<BatchItem[]>(`/api/csv/batches/${id}/items`),

  // ---- System / observability ----
  systemQuota: () => api.get<{ quotas: QuotaEntry[] }>("/api/system/quota"),
  systemStats: () => api.get<SystemStats>("/api/system/stats"),
  systemBreakers: () =>
    api.get<{ breakers: BreakerEntry[] }>("/api/system/breakers"),
  schedulerJobs: () => api.get<SchedulerJobStatus[]>("/api/system/jobs"),
  runSchedulerJob: (name: string, opts: { lookbackDays?: number } = {}) =>
    api.post<{ job: string; status: string; lookback_days?: number }>(
      `/api/system/jobs/${encodeURIComponent(name)}/run`,
      opts.lookbackDays ? { lookback_days: opts.lookbackDays } : undefined,
    ),
  auditRecent: (limit = 50) =>
    api.get<Page<AuditEntry>>(`/api/audit/recent?${qs({ limit })}`),
  // v2.2.4 — audit search. All filters optional; pass undefined to omit.
  // since/until are RFC3339 strings.
  // v2.13.x — was sending ?offset=, but the backend's parsePagination
  // (helpers.go) only reads ?page=&limit= and derives OFFSET itself, so
  // pages 2..N silently returned page 1 forever. Switched to ?page= to
  // match the page-based contract every other list endpoint (e.g. plans)
  // already uses.
  auditSearch: (filters: AuditSearchFilters, limit = 100, page = 1) =>
    api.get<Page<AuditEntry>>(
      `/api/audit/search?${qs({ ...filters, limit, page })}`,
    ),
  auditActors: () => api.get<string[]>("/api/audit/actors"),
  auditActions: () =>
    api.get<{ action: string; occurrence_count: number }[]>(
      "/api/audit/actions",
    ),

  // ---- Investment Plans (v2.12 投放工作台) ----
  plans: (params: { status?: string; page?: number; limit?: number } = {}) =>
    api.get<Page<PlanSummary>>(`/api/plans?${qs({ ...params })}`),
  plan: (id: number) =>
    api.get<PlanDetailResponse>(`/api/plans/${id}`),
  createPlan: (body: CreatePlanRequest) =>
    api.post<PlanSummary>("/api/plans", body),
  updatePlan: (id: number, body: UpdatePlanRequest) =>
    api.patch<PlanSummary>(`/api/plans/${id}`, body),
  archivePlan: (id: number) =>
    api.post<PlanSummary>(`/api/plans/${id}/archive`),
  importPlanCandidates: (id: number, list: string) =>
    api.post<PlanImportResponse>(`/api/plans/${id}/import`, { list }),
  promotePlan: (id: number, body: PromotePlanRequest = {}) =>
    api.post<PromotePlanResponse>(`/api/plans/${id}/promote`, body),
  updatePlanCandidate: (
    planID: number,
    candidateID: number,
    body: UpdatePlanCandidateRequest,
  ) =>
    api.patch<PlanCandidate>(
      `/api/plans/${planID}/candidates/${candidateID}`,
      body,
    ),
  removePlanCandidate: (planID: number, candidateID: number) =>
    api.delete<void>(`/api/plans/${planID}/candidates/${candidateID}`),
  // Returns a Markdown blob for download — bypass the JSON parser so the
  // caller can pipe straight to a Blob URL.
  planExportURL: (id: number) => `/api/plans/${id}/export.md`,

  // ---- Dashboard (sidebar badges) ----
  // v2.1.0 — counts of items needing human action. Layout polls this every
  // 30s; nav chips render when a count is > 0.
  dashboardBadges: () => api.get<DashboardBadges>("/api/dashboard/badges"),
  // v2.2.5 — operator-tunable knobs reference (Settings page).
  systemConfigKnobs: () =>
    api.get<{ knobs: ConfigKnob[]; hot_reload_hint: string }>(
      "/api/system/config-knobs",
    ),
};

// v2.2.5 — read-only reference of operator-tunable config values.
// Each knob lists current value + where/how to edit it.
export interface ConfigKnob {
  path: string;
  value: unknown;
  type: "float" | "int" | "string";
  category: string;
  description: string;
  edit_hint: string;
}

export interface DashboardBadges {
  evaluation_hub: number;
  sponsorship_review: number;
  discovery: number;
  // v2.1.8 — dead-cookie count from sidecar pool (cookies_health_check cron
  // snapshot in system_state). 0 if sidecar offline or never polled yet.
  x_accounts: number;
  // v2.6.18 — YouTube cookies probe outcome. true = yt-dlp can't extract
  // a canary video with the current cookies file → archive cron will fail
  // until operator refreshes cookies. Surfaced as a header pill, not a
  // nav badge, because no /youtube-cookies management page exists yet.
  youtube_cookies_dead: boolean;
  youtube_cookies_reason?: string;
}

// sha256Hex mirrors Go's server-side hashing of the CSV body so the UI can
// query the batch row after upload via SubtleCrypto.
export async function sha256Hex(text: string): Promise<string> {
  const buf = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
